import React, { useState, useEffect } from 'react';
import {
  FaChartBar,
  FaUsers,
  FaMapMarkedAlt,
  FaDollarSign,
  FaStar,
  FaCamera,
  FaComments,
  FaUserPlus,
  FaCrown,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaRobot,
  FaSync,
  FaGlobe,
  FaEye,
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaChevronLeft,
  FaChevronRight,
  FaNewspaper,
  FaBookOpen,
  FaTimes
} from 'react-icons/fa';
import { getAnalyticsDashboard, getVisitorLogs, getVisitorByIp, getUsersList, getItinerariesList, getSubscriptionsList, getRatingsList, getPhotosList, getChatModificationsList, getGroupActivityList, getUserActivity, getSiteStatistics, getCurrentStatistics, getBlogVisitors, getPageDrilldown } from '../api/client';

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(null); // null = all time, 7, 30, 90 days
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'visitors'

  // Visitor log state
  const [visitorLogs, setVisitorLogs] = useState(null);
  const [visitorLoading, setVisitorLoading] = useState(false);
  const [visitorPage, setVisitorPage] = useState(0);
  const [visitorFilters, setVisitorFilters] = useState({
    ip: '',
    path: '',
    method: '',
    country: ''
  });
  const [selectedIp, setSelectedIp] = useState(null);
  const [ipDetails, setIpDetails] = useState(null);
  const [ipDetailsLoading, setIpDetailsLoading] = useState(false);

  // Drill-down modal states
  const [drilldownModal, setDrilldownModal] = useState(null); // 'users', 'itineraries', 'destinations', etc.
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownPage, setDrilldownPage] = useState(0);
  const [drilldownFilter, setDrilldownFilter] = useState(null);

  // User activity modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [userActivityPage, setUserActivityPage] = useState(0);

  // Site statistics states
  const [siteStats, setSiteStats] = useState(null);
  const [siteStatsLoading, setSiteStatsLoading] = useState(false);

  // Current statistics states (real-time)
  const [currentStats, setCurrentStats] = useState(null);
  const [currentStatsLoading, setCurrentStatsLoading] = useState(false);
  const [currentStatsModal, setCurrentStatsModal] = useState(null); // 'surfing', 'sessions', 'users'
  const [currentStatsDetails, setCurrentStatsDetails] = useState(null);
  const [currentStatsDetailsLoading, setCurrentStatsDetailsLoading] = useState(false);

  // Blog visitors state
  const [blogVisitors, setBlogVisitors] = useState(null);
  const [blogVisitorsLoading, setBlogVisitorsLoading] = useState(false);
  const [blogVisitorsExpanded, setBlogVisitorsExpanded] = useState(false);
  const [blogSelectedSlug, setBlogSelectedSlug] = useState(null);
  const [blogSelectedIp, setBlogSelectedIp] = useState(null);
  const [blogVisitorPage, setBlogVisitorPage] = useState(0);

  // Page drilldown state
  const [pageDrilldown, setPageDrilldown] = useState(null);
  const [pageDrilldownLoading, setPageDrilldownLoading] = useState(false);
  const [pageDrilldownPage, setPageDrilldownPage] = useState(0);
  const [selectedPagePath, setSelectedPagePath] = useState(null);

  const LOGS_PER_PAGE = 50;
  const DRILLDOWN_PER_PAGE = 50;

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  useEffect(() => {
    if (activeTab === 'visitors') {
      loadVisitorLogs();
    }
  }, [activeTab, visitorPage, period]);

  useEffect(() => {
    if (activeTab === 'overview' && data) {
      loadSiteStatistics();
    }
  }, [activeTab, period, data]);

  // Load current statistics with auto-refresh every 30 seconds
  useEffect(() => {
    if (activeTab === 'overview') {
      loadCurrentStatistics();
      const interval = setInterval(loadCurrentStatistics, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Load blog visitors when overview tab is active
  useEffect(() => {
    if (activeTab === 'overview' && data) {
      loadBlogVisitors(blogSelectedSlug, blogSelectedIp, blogVisitorPage);
    }
  }, [activeTab, period, data, blogSelectedSlug, blogSelectedIp, blogVisitorPage]);

  const loadCurrentStatistics = async () => {
    setCurrentStatsLoading(true);
    try {
      const result = await getCurrentStatistics();
      setCurrentStats(result);
    } catch (err) {
      console.error('Error loading current statistics:', err);
    } finally {
      setCurrentStatsLoading(false);
    }
  };

  const loadBlogVisitors = async (slug = null, ip = null, page = 0) => {
    setBlogVisitorsLoading(true);
    try {
      const result = await getBlogVisitors({
        days: period,
        limit: LOGS_PER_PAGE,
        offset: page * LOGS_PER_PAGE,
        slug: slug || undefined,
        ip: ip || undefined
      });
      setBlogVisitors(result);
    } catch (err) {
      console.error('Error loading blog visitors:', err);
    } finally {
      setBlogVisitorsLoading(false);
    }
  };

  const loadPageDrilldown = async (path, page = 0) => {
    setPageDrilldownLoading(true);
    setSelectedPagePath(path);
    try {
      const result = await getPageDrilldown({
        path,
        days: period,
        limit: LOGS_PER_PAGE,
        offset: page * LOGS_PER_PAGE
      });
      setPageDrilldown(result);
      setPageDrilldownPage(page);
    } catch (err) {
      console.error('Error loading page drilldown:', err);
    } finally {
      setPageDrilldownLoading(false);
    }
  };

  const closePageDrilldown = () => {
    setPageDrilldown(null);
    setSelectedPagePath(null);
    setPageDrilldownPage(0);
  };

  const openCurrentStatsModal = async (type) => {
    setCurrentStatsModal(type);
    setCurrentStatsDetailsLoading(true);
    try {
      const result = await getCurrentStatistics(type);
      setCurrentStatsDetails(result);
    } catch (err) {
      console.error('Error loading current stats details:', err);
    } finally {
      setCurrentStatsDetailsLoading(false);
    }
  };

  const closeCurrentStatsModal = () => {
    setCurrentStatsModal(null);
    setCurrentStatsDetails(null);
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getAnalyticsDashboard(period);
      setData(result);
    } catch (err) {
      console.error('Error loading analytics:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to analytics.');
      } else {
        setError('Failed to load analytics. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVisitorLogs = async () => {
    setVisitorLoading(true);
    try {
      const result = await getVisitorLogs({
        days: period,
        limit: LOGS_PER_PAGE,
        offset: visitorPage * LOGS_PER_PAGE,
        ip: visitorFilters.ip || undefined,
        path: visitorFilters.path || undefined,
        method: visitorFilters.method || undefined,
        country: visitorFilters.country || undefined
      });
      setVisitorLogs(result);
    } catch (err) {
      console.error('Error loading visitor logs:', err);
    } finally {
      setVisitorLoading(false);
    }
  };

  const loadIpDetails = async (ip) => {
    setSelectedIp(ip);
    setIpDetailsLoading(true);
    try {
      const result = await getVisitorByIp(ip, period);
      setIpDetails(result);
    } catch (err) {
      console.error('Error loading IP details:', err);
    } finally {
      setIpDetailsLoading(false);
    }
  };

  const openDrilldown = async (type, filter = null) => {
    setDrilldownModal(type);
    setDrilldownFilter(filter);
    setDrilldownPage(0);
    setDrilldownLoading(true);
    try {
      let result;
      if (type === 'users') {
        result = await getUsersList({ days: period, tier: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'itineraries') {
        result = await getItinerariesList({ days: period, status: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'subscriptions') {
        result = await getSubscriptionsList({ days: period, event_type: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'ratings') {
        result = await getRatingsList({ days: period, rating: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'photos') {
        result = await getPhotosList({ days: period, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'chat-modifications') {
        result = await getChatModificationsList({ days: period, applied: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      } else if (type === 'group-activity') {
        result = await getGroupActivityList({ days: period, activity_type: filter, limit: DRILLDOWN_PER_PAGE, offset: 0 });
      }
      setDrilldownData(result);
    } catch (err) {
      console.error(`Error loading ${type} drilldown:`, err);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const loadDrilldownPage = async (newPage) => {
    setDrilldownPage(newPage);
    setDrilldownLoading(true);
    try {
      let result;
      if (drilldownModal === 'users') {
        result = await getUsersList({ days: period, tier: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'itineraries') {
        result = await getItinerariesList({ days: period, status: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'subscriptions') {
        result = await getSubscriptionsList({ days: period, event_type: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'ratings') {
        result = await getRatingsList({ days: period, rating: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'photos') {
        result = await getPhotosList({ days: period, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'chat-modifications') {
        result = await getChatModificationsList({ days: period, applied: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      } else if (drilldownModal === 'group-activity') {
        result = await getGroupActivityList({ days: period, activity_type: drilldownFilter, limit: DRILLDOWN_PER_PAGE, offset: newPage * DRILLDOWN_PER_PAGE });
      }
      setDrilldownData(result);
    } catch (err) {
      console.error(`Error loading drilldown page:`, err);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const closeDrilldown = () => {
    setDrilldownModal(null);
    setDrilldownData(null);
    setDrilldownFilter(null);
    setDrilldownPage(0);
  };

  // Load user activity when clicking on a user
  const loadUserActivity = async (userId, userName) => {
    setSelectedUser({ id: userId, name: userName });
    setUserActivityLoading(true);
    setUserActivityPage(0);
    try {
      const result = await getUserActivity(userId, { days: period, limit: 50, offset: 0 });
      setUserActivity(result);
    } catch (err) {
      console.error('Error loading user activity:', err);
    } finally {
      setUserActivityLoading(false);
    }
  };

  const loadUserActivityPage = async (newPage) => {
    setUserActivityPage(newPage);
    setUserActivityLoading(true);
    try {
      const result = await getUserActivity(selectedUser.id, {
        days: period,
        limit: 50,
        offset: newPage * 50
      });
      setUserActivity(result);
    } catch (err) {
      console.error('Error loading user activity page:', err);
    } finally {
      setUserActivityLoading(false);
    }
  };

  const closeUserActivity = () => {
    setSelectedUser(null);
    setUserActivity(null);
    setUserActivityPage(0);
  };

  // Load site statistics
  const loadSiteStatistics = async () => {
    setSiteStatsLoading(true);
    try {
      const result = await getSiteStatistics(period);
      setSiteStats(result);
    } catch (err) {
      console.error('Error loading site statistics:', err);
    } finally {
      setSiteStatsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setVisitorFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setVisitorPage(0);
    loadVisitorLogs();
  };

  const clearFilters = () => {
    setVisitorFilters({ ip: '', path: '', method: '', country: '' });
    setVisitorPage(0);
    setTimeout(loadVisitorLogs, 0);
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'primary', onClick }) => {
    const colorClasses = {
      primary: 'from-primary-500 to-primary-600',
      secondary: 'from-secondary-500 to-secondary-600',
      green: 'from-green-500 to-green-600',
      amber: 'from-amber-500 to-amber-600',
      purple: 'from-purple-500 to-purple-600',
      pink: 'from-pink-500 to-pink-600',
      cyan: 'from-cyan-500 to-cyan-600'
    };

    return (
      <div
        className={`bg-white rounded-xl shadow-medium p-6 hover:shadow-elevated transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-white`}>
            <Icon className="text-xl" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 font-medium">{label}</p>
            <p className="text-2xl font-bold text-neutral-800">{value}</p>
            {subValue && <p className="text-xs text-neutral-400">{subValue}</p>}
          </div>
        </div>
      </div>
    );
  };

  const TierBadge = ({ tier, count, onClick }) => {
    const tierColors = {
      free: 'bg-neutral-100 text-neutral-700',
      explorer: 'bg-neutral-100 text-neutral-700',
      individual: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
      group: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
      starter_agent: 'bg-gradient-to-r from-teal-500 to-teal-600 text-white',
      professional_agent: 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white',
      enterprise_agency: 'bg-gradient-to-r from-orange-500 to-orange-600 text-white',
      promotional: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
      admin: 'bg-gradient-to-r from-red-500 to-red-600 text-white',
    };

    return (
      <div
        className={`flex items-center justify-between py-2 ${onClick ? 'cursor-pointer hover:bg-neutral-50 rounded-lg px-2 -mx-2 transition-colors' : ''}`}
        onClick={onClick}
      >
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${tierColors[tier] || tierColors.explorer}`}>
          {!['demo', 'explorer'].includes(tier) && <FaCrown className="inline mr-1 text-yellow-300" />}
          {tier.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-neutral-700">{count}</span>
          {onClick && <FaChevronRight className="text-neutral-400 text-xs" />}
        </div>
      </div>
    );
  };

  const RatingBar = ({ rating, count, maxCount }) => {
    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 w-16">
          <span className="text-sm font-medium text-neutral-600">{rating}</span>
          <FaStar className="text-amber-400 text-sm" />
        </div>
        <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm text-neutral-500 w-8 text-right">{count}</span>
      </div>
    );
  };

  const MethodBadge = ({ method }) => {
    const colors = {
      GET: 'bg-green-100 text-green-700',
      POST: 'bg-blue-100 text-blue-700',
      PUT: 'bg-amber-100 text-amber-700',
      PATCH: 'bg-purple-100 text-purple-700',
      DELETE: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${colors[method] || 'bg-neutral-100 text-neutral-700'}`}>
        {method}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    let color = 'bg-neutral-100 text-neutral-700';
    if (status >= 200 && status < 300) color = 'bg-green-100 text-green-700';
    else if (status >= 300 && status < 400) color = 'bg-blue-100 text-blue-700';
    else if (status >= 400 && status < 500) color = 'bg-amber-100 text-amber-700';
    else if (status >= 500) color = 'bg-red-100 text-red-700';

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${color}`}>
        {status}
      </span>
    );
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    // Ensure UTC dates from backend are properly converted to user's local timezone
    let dateStr = isoString;
    // If the date string doesn't have timezone info, assume it's UTC
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
      dateStr = dateStr + 'Z';
    }
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // IP Details Modal/Panel
  const IpDetailsPanel = () => {
    if (!selectedIp) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaNetworkWired className="text-xl" />
              <div>
                <h3 className="font-bold text-lg">{selectedIp}</h3>
                {ipDetails?.location?.city && (
                  <p className="text-sm text-white/80">
                    {ipDetails.location.city}, {ipDetails.location.region}, {ipDetails.location.country}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => { setSelectedIp(null); setIpDetails(null); }}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaArrowLeft />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {ipDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSync className="text-2xl text-cyan-500 animate-spin" />
              </div>
            ) : ipDetails ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-cyan-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-cyan-700">{ipDetails.visits}</p>
                    <p className="text-sm text-cyan-600">Total Visits</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700">{ipDetails.location?.isp || '-'}</p>
                    <p className="text-xs text-neutral-500">ISP</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700">{formatDateTime(ipDetails.first_visit)}</p>
                    <p className="text-xs text-neutral-500">First Visit</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700">{formatDateTime(ipDetails.last_visit)}</p>
                    <p className="text-xs text-neutral-500">Last Visit</p>
                  </div>
                </div>

                {/* Location Details */}
                {ipDetails.location && (ipDetails.location.latitude || ipDetails.location.longitude) && (
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h4 className="font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                      <FaMapMarkerAlt className="text-red-500" /> Location
                    </h4>
                    <p className="text-sm text-neutral-600">
                      {[ipDetails.location.city, ipDetails.location.region, ipDetails.location.country].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-xs text-neutral-400 mt-1">
                      Lat: {ipDetails.location.latitude}, Lng: {ipDetails.location.longitude}
                      {ipDetails.location.timezone && ` | Timezone: ${ipDetails.location.timezone}`}
                    </p>
                  </div>
                )}

                {/* Methods Used */}
                <div>
                  <h4 className="font-semibold text-neutral-700 mb-2">HTTP Methods</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ipDetails.methods || {}).map(([method, count]) => (
                      <div key={method} className="flex items-center gap-2 bg-neutral-50 px-3 py-1.5 rounded-lg">
                        <MethodBadge method={method} />
                        <span className="text-sm font-semibold text-neutral-700">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pages Visited */}
                <div>
                  <h4 className="font-semibold text-neutral-700 mb-2">Top Pages Visited</h4>
                  <div className="bg-neutral-50 rounded-lg divide-y divide-neutral-100">
                    {(ipDetails.pages_visited || []).slice(0, 10).map((page, idx) => (
                      <div key={idx} className="px-4 py-2 flex items-center justify-between">
                        <code className="text-sm text-neutral-600 truncate flex-1">{page.path}</code>
                        <span className="text-sm font-semibold text-neutral-700 ml-2">{page.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h4 className="font-semibold text-neutral-700 mb-2">Recent Activity</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Time</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Method</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Path</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Status</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Time (ms)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {(ipDetails.recent_logs || []).map((log) => (
                          <tr key={log.id} className="hover:bg-neutral-50">
                            <td className="px-3 py-2 text-neutral-500 text-xs">{formatDateTime(log.created_at)}</td>
                            <td className="px-3 py-2"><MethodBadge method={log.method} /></td>
                            <td className="px-3 py-2 font-mono text-xs truncate max-w-xs">{log.path}</td>
                            <td className="px-3 py-2"><StatusBadge status={log.status_code} /></td>
                            <td className="px-3 py-2 text-neutral-600">{log.response_time_ms}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-neutral-500 py-8">No data available</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // User Activity Modal
  const UserActivityModal = () => {
    if (!selectedUser) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaUsers className="text-xl" />
              <div>
                <h3 className="font-bold text-lg">User Activity</h3>
                {userActivity?.user && (
                  <p className="text-sm text-white/80">
                    {userActivity.user.full_name || userActivity.user.email}
                    {userActivity.user.member_code && ` (#${userActivity.user.member_code})`}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={closeUserActivity}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaArrowLeft />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {userActivityLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSync className="text-2xl text-primary-500 animate-spin" />
              </div>
            ) : userActivity ? (
              <div className="space-y-6">
                {/* User Info Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-primary-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-primary-700">{userActivity.total}</p>
                    <p className="text-sm text-primary-600">Total Page Views</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700">{userActivity.user?.email || '-'}</p>
                    <p className="text-xs text-neutral-500">Email</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700 capitalize">{(userActivity.user?.subscription_tier || 'demo').replace(/_/g, ' ')}</p>
                    <p className="text-xs text-neutral-500">Subscription</p>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4 text-center">
                    <p className="text-sm font-semibold text-neutral-700">#{userActivity.user?.member_code || '-'}</p>
                    <p className="text-xs text-neutral-500">Member Code</p>
                  </div>
                </div>

                {/* Activity Table */}
                <div>
                  <h4 className="font-semibold text-neutral-700 mb-2">Pages Accessed</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Date/Time</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Page</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Method</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Status</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">IP Address</th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {(userActivity.activity || []).map((log) => (
                          <tr key={log.id} className="hover:bg-neutral-50">
                            <td className="px-3 py-2 text-neutral-500 text-xs whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                            <td className="px-3 py-2 font-mono text-xs text-neutral-600 max-w-xs truncate" title={log.path}>{log.path}</td>
                            <td className="px-3 py-2"><MethodBadge method={log.method} /></td>
                            <td className="px-3 py-2"><StatusBadge status={log.status_code} /></td>
                            <td className="px-3 py-2 font-mono text-xs text-neutral-600">{log.ip_address}</td>
                            <td className="px-3 py-2 text-xs text-neutral-600">
                              {[log.city, log.country].filter(Boolean).join(', ') || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {userActivity.total > 50 && (
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                    <p className="text-sm text-neutral-500">
                      Showing {userActivityPage * 50 + 1} - {Math.min((userActivityPage + 1) * 50, userActivity.total)} of {userActivity.total.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadUserActivityPage(Math.max(0, userActivityPage - 1))}
                        disabled={userActivityPage === 0 || userActivityLoading}
                        className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaChevronLeft />
                      </button>
                      <span className="text-sm text-neutral-600">
                        Page {userActivityPage + 1} of {Math.ceil(userActivity.total / 50)}
                      </span>
                      <button
                        onClick={() => loadUserActivityPage(userActivityPage + 1)}
                        disabled={(userActivityPage + 1) * 50 >= userActivity.total || userActivityLoading}
                        className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-neutral-500 py-8">No activity data available</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Drill-down Modal
  const DrilldownModal = () => {
    if (!drilldownModal) return null;

    const titles = {
      users: 'Users List',
      itineraries: 'Itineraries List',
      subscriptions: 'Subscription Events',
      ratings: 'Itinerary Ratings',
      photos: 'Photo Uploads',
      'chat-modifications': 'AI Chat Modifications',
      'group-activity': 'Group Collaboration Activity'
    };

    const filterLabels = {
      users: drilldownFilter
        ? (drilldownFilter === 'paid' ? 'Paid Users (Non-Explorer)' : `Tier: ${drilldownFilter.charAt(0).toUpperCase() + drilldownFilter.slice(1)}`)
        : 'All Users',
      itineraries: drilldownFilter ? `Status: ${drilldownFilter.charAt(0).toUpperCase() + drilldownFilter.slice(1)}` : 'All Itineraries',
      subscriptions: drilldownFilter ? `Event: ${drilldownFilter.replace('_', ' ')}` : 'All Events',
      ratings: drilldownFilter ? `${drilldownFilter} Star Ratings` : 'All Ratings',
      photos: 'All Photos',
      'chat-modifications': drilldownFilter !== null ? (drilldownFilter ? 'Applied Modifications' : 'Unapplied Modifications') : 'All Modifications',
      'group-activity': drilldownFilter ? `Type: ${drilldownFilter}` : 'All Activity'
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">{titles[drilldownModal]}</h3>
              <p className="text-sm text-white/80">{filterLabels[drilldownModal]} {period ? `(Last ${period} days)` : '(All time)'}</p>
            </div>
            <button
              onClick={closeDrilldown}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaArrowLeft />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {drilldownLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSync className="text-2xl text-primary-500 animate-spin" />
              </div>
            ) : drilldownData ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-neutral-50 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Total: <strong>{drilldownData.total?.toLocaleString()}</strong></span>
                  {drilldownLoading && <FaSync className="text-primary-500 animate-spin" />}
                </div>

                {/* Users List */}
                {drilldownModal === 'users' && drilldownData.users && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Member Code</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Name</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Email</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Tier</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Status</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Itineraries</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.users.map((user) => (
                          <tr key={user.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 font-mono text-xs text-primary-600">#{user.member_code}</td>
                            <td className="px-4 py-3 font-medium text-neutral-800">{user.full_name || '-'}</td>
                            <td className="px-4 py-3 text-neutral-600">{user.email}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                !['demo', 'explorer'].includes(user.subscription_tier)
                                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                                  : 'bg-neutral-100 text-neutral-700'
                              }`}>
                                {!['demo', 'explorer'].includes(user.subscription_tier) && <FaCrown className="inline mr-1 text-yellow-300" />}
                                {user.subscription_tier?.charAt(0).toUpperCase() + user.subscription_tier?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-neutral-700">{user.itinerary_count}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(user.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Itineraries List */}
                {drilldownModal === 'itineraries' && drilldownData.itineraries && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">ID</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">From</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">To</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Interests</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Status</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Iterations</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600 whitespace-nowrap">Created</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.itineraries.map((itin) => (
                          <tr key={itin.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 font-mono text-xs text-secondary-600">{itin.request_id?.slice(0, 8)}...</td>
                            <td className="px-4 py-3 text-neutral-600 text-xs">{itin.user_email}</td>
                            <td className="px-4 py-3 text-neutral-700 text-xs max-w-[150px] truncate" title={itin.start_location}>{itin.start_location}</td>
                            <td className="px-4 py-3 text-neutral-700 text-xs max-w-[150px] truncate" title={itin.end_location}>{itin.end_location}</td>
                            <td className="px-4 py-3 text-xs">
                              <div className="flex flex-wrap gap-1">
                                {(() => {
                                  const interests = Array.isArray(itin.interests)
                                    ? itin.interests
                                    : (itin.interests || '').split(',').map(s => s.trim()).filter(Boolean);
                                  return (
                                    <>
                                      {interests.slice(0, 2).map((interest, idx) => (
                                        <span key={idx} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs">
                                          {interest}
                                        </span>
                                      ))}
                                      {interests.length > 2 && (
                                        <span className="text-neutral-400 text-xs">+{interests.length - 2}</span>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                itin.status === 'completed' ? 'bg-green-100 text-green-700' :
                                itin.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                itin.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                itin.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-neutral-100 text-neutral-700'
                              }`}>
                                {itin.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center font-mono text-neutral-600">{itin.optimization_iterations || 0}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">{formatDateTime(itin.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Subscriptions List */}
                {drilldownModal === 'subscriptions' && drilldownData.events && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Event Type</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Previous Tier</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">New Tier</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Source</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.events.map((event) => (
                          <tr key={event.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 text-neutral-600 text-xs">{event.user_email}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                event.event_type === 'created' ? 'bg-green-100 text-green-700' :
                                event.event_type === 'cancelled' ? 'bg-red-100 text-red-700' :
                                event.event_type === 'upgraded' ? 'bg-blue-100 text-blue-700' :
                                'bg-neutral-100 text-neutral-700'
                              }`}>
                                {event.event_type?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-neutral-600 text-xs capitalize">{event.previous_tier || '-'}</td>
                            <td className="px-4 py-3 text-neutral-600 text-xs capitalize">{event.new_tier || '-'}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{event.source}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(event.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Ratings List */}
                {drilldownModal === 'ratings' && drilldownData.ratings && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Destination</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Rating</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Reaction</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Recommend</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Comment</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.ratings.map((rating) => (
                          <tr key={rating.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 text-neutral-600 text-xs">{rating.user_email}</td>
                            <td className="px-4 py-3 text-neutral-700 text-xs max-w-[150px] truncate">{rating.itinerary_destination}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar key={i} className={`text-xs ${i < rating.rating ? 'text-amber-400' : 'text-neutral-200'}`} />
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-neutral-600 text-xs capitalize">{rating.emoji_reaction || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                rating.would_recommend ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                              }`}>
                                {rating.would_recommend ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-neutral-500 text-xs max-w-[200px] truncate" title={rating.comment}>{rating.comment || '-'}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(rating.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Photos List */}
                {drilldownModal === 'photos' && drilldownData.photos && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Filename</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Uploaded By</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Destination</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Comment</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Uploaded At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.photos.map((photo) => (
                          <tr key={photo.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 font-mono text-xs text-neutral-600">{photo.filename}</td>
                            <td className="px-4 py-3 text-neutral-600 text-xs">{photo.user_email}</td>
                            <td className="px-4 py-3 text-neutral-700 text-xs max-w-[150px] truncate">{photo.itinerary_destination}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs max-w-[150px] truncate">{photo.comment || '-'}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(photo.uploaded_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Chat Modifications List */}
                {drilldownModal === 'chat-modifications' && drilldownData.modifications && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User Query</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Modification</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Destination</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Applied</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.modifications.map((mod) => (
                          <tr key={mod.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 text-neutral-600 text-xs max-w-[200px] truncate" title={mod.user_query}>{mod.user_query}</td>
                            <td className="px-4 py-3 text-neutral-700 text-xs max-w-[200px] truncate" title={mod.modification_description}>{mod.modification_description}</td>
                            <td className="px-4 py-3 text-neutral-600 text-xs max-w-[120px] truncate">{mod.itinerary_destination}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                mod.applied ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                              }`}>
                                {mod.applied ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(mod.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Group Activity List */}
                {drilldownModal === 'group-activity' && drilldownData.activities && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Type</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Details</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Destination</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {drilldownData.activities.map((activity, idx) => (
                          <tr key={idx} className="hover:bg-neutral-50">
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                activity.type === 'invite' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {activity.type === 'invite' ? 'Invite' : 'Message'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-neutral-600 text-xs">
                              {activity.type === 'invite' ? (
                                <span>
                                  <strong>{activity.inviter_email}</strong> invited <strong>{activity.invitee_email}</strong>
                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                    activity.status === 'accepted' ? 'bg-green-100 text-green-700' :
                                    activity.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    'bg-neutral-100 text-neutral-600'
                                  }`}>
                                    {activity.status}
                                  </span>
                                </span>
                              ) : (
                                <span>
                                  <strong>{activity.user_email}</strong>: {activity.message_preview}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-neutral-600 text-xs max-w-[120px] truncate">{activity.itinerary_destination}</td>
                            <td className="px-4 py-3 text-neutral-500 text-xs">{formatDateTime(activity.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {drilldownData.total > DRILLDOWN_PER_PAGE && (
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                    <p className="text-sm text-neutral-500">
                      Showing {drilldownPage * DRILLDOWN_PER_PAGE + 1} - {Math.min((drilldownPage + 1) * DRILLDOWN_PER_PAGE, drilldownData.total)} of {drilldownData.total?.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadDrilldownPage(Math.max(0, drilldownPage - 1))}
                        disabled={drilldownPage === 0 || drilldownLoading}
                        className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaChevronLeft />
                      </button>
                      <span className="text-sm text-neutral-600">
                        Page {drilldownPage + 1} of {Math.ceil(drilldownData.total / DRILLDOWN_PER_PAGE)}
                      </span>
                      <button
                        onClick={() => loadDrilldownPage(drilldownPage + 1)}
                        disabled={(drilldownPage + 1) * DRILLDOWN_PER_PAGE >= drilldownData.total || drilldownLoading}
                        className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-neutral-500 py-8">No data available</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Page Drilldown Modal - shows detailed visitor info for a specific page
  const PageDrilldownModal = () => {
    if (!pageDrilldown) return null;

    const totalPages = Math.ceil((pageDrilldown.total || 0) / LOGS_PER_PAGE);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-violet-600 text-white flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">{pageDrilldown.page_name || pageDrilldown.path}</h3>
              <p className="text-sm text-white/80">
                {pageDrilldown.total} total views ({pageDrilldown.user_count} users, {pageDrilldown.bot_count} bots)
                {period ? ` • Last ${period} days` : ' • All time'}
              </p>
            </div>
            <button
              onClick={closePageDrilldown}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaTimes />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {pageDrilldownLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSync className="text-2xl text-purple-500 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-neutral-50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex gap-6">
                    <span className="text-sm text-neutral-600">
                      Total: <strong>{pageDrilldown.total?.toLocaleString()}</strong>
                    </span>
                    <span className="text-sm text-green-600">
                      Users: <strong>{pageDrilldown.user_count?.toLocaleString()}</strong>
                    </span>
                    <span className="text-sm text-orange-500">
                      Bots: <strong>{pageDrilldown.bot_count?.toLocaleString()}</strong>
                    </span>
                  </div>
                </div>

                {/* Visitors Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-100">
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">IP Address</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date & Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">Location</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                        <th className="px-4 py-3 text-left font-semibold text-neutral-600">User Agent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {pageDrilldown.visitors?.map((visitor) => (
                        <tr key={visitor.id} className="hover:bg-neutral-50">
                          <td className="px-4 py-3 font-mono text-xs text-primary-600">{visitor.ip_address}</td>
                          <td className="px-4 py-3 text-neutral-600">{formatDateTime(visitor.accessed_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              visitor.is_bot
                                ? 'bg-orange-100 text-orange-700'
                                : visitor.user
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                            }`}>
                              {visitor.is_bot && <FaRobot className="inline mr-1" />}
                              {visitor.visitor_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-neutral-600">
                            {visitor.city && visitor.country
                              ? `${visitor.city}, ${visitor.country}`
                              : visitor.country || '-'}
                          </td>
                          <td className="px-4 py-3 text-neutral-600">
                            {visitor.user ? (
                              <span className="text-primary-600 font-medium">{visitor.user.full_name || visitor.user.email}</span>
                            ) : (
                              <span className="text-neutral-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 text-xs max-w-xs truncate" title={visitor.user_agent}>
                            {visitor.user_agent || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-neutral-600">
                      Page {pageDrilldownPage + 1} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadPageDrilldown(selectedPagePath, pageDrilldownPage - 1)}
                        disabled={pageDrilldownPage === 0}
                        className="px-3 py-1 bg-neutral-100 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-200"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        onClick={() => loadPageDrilldown(selectedPagePath, pageDrilldownPage + 1)}
                        disabled={pageDrilldownPage >= totalPages - 1}
                        className="px-3 py-1 bg-neutral-100 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-200"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Current Stats Modal for drill-down
  const CurrentStatsModal = () => {
    if (!currentStatsModal) return null;

    const titles = {
      surfing: 'Currently Surfing',
      sessions: 'Current Sessions',
      users: 'Concurrent Users'
    };

    const descriptions = {
      surfing: 'Visitors active in the last 5 minutes',
      sessions: 'All sessions active in the last 15 minutes',
      users: 'Logged-in users active in the last 5 minutes'
    };

    const gradients = {
      surfing: 'from-green-500 to-emerald-600',
      sessions: 'from-blue-500 to-cyan-600',
      users: 'from-purple-500 to-violet-600'
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className={`px-6 py-4 bg-gradient-to-r ${gradients[currentStatsModal]} text-white flex items-center justify-between`}>
            <div>
              <h3 className="font-bold text-lg">{titles[currentStatsModal]}</h3>
              <p className="text-sm text-white/80">{descriptions[currentStatsModal]}</p>
            </div>
            <button
              onClick={closeCurrentStatsModal}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FaArrowLeft />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {currentStatsDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSync className="text-2xl text-primary-500 animate-spin" />
              </div>
            ) : currentStatsDetails ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-neutral-50 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm text-neutral-600">
                    Total: <strong>
                      {currentStatsModal === 'surfing' && currentStatsDetails.currently_surfing}
                      {currentStatsModal === 'sessions' && currentStatsDetails.current_sessions}
                      {currentStatsModal === 'users' && currentStatsDetails.concurrent_users}
                    </strong>
                  </span>
                  <span className="text-xs text-neutral-400">
                    Last updated: {formatDateTime(currentStatsDetails.timestamp)}
                  </span>
                </div>

                {/* Surfing Details */}
                {currentStatsModal === 'surfing' && currentStatsDetails.surfing_details && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">IP Address</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Location</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Last Page</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Requests</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600 whitespace-nowrap">Last Seen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {currentStatsDetails.surfing_details.map((visitor, idx) => (
                          <tr key={idx} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 font-mono text-xs text-neutral-600">{visitor.ip_address}</td>
                            <td className="px-4 py-3">
                              {visitor.user ? (
                                <div>
                                  <p className="font-medium text-neutral-800 text-xs">{visitor.user.full_name || visitor.user.email}</p>
                                  <p className="text-xs text-neutral-400">#{visitor.user.member_code}</p>
                                </div>
                              ) : (
                                <span className="text-neutral-400 text-xs">Guest</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-neutral-600">
                              {visitor.city && visitor.country ? `${visitor.city}, ${visitor.country}` : visitor.country || '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-neutral-600 max-w-[200px] truncate" title={visitor.last_page}>
                              {visitor.last_page || '-'}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-neutral-700">{visitor.request_count}</td>
                            <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDateTime(visitor.last_seen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {currentStatsDetails.surfing_details.length === 0 && (
                      <p className="text-center text-neutral-500 py-8">No visitors currently surfing</p>
                    )}
                  </div>
                )}

                {/* Sessions Details */}
                {currentStatsModal === 'sessions' && currentStatsDetails.sessions_details && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">IP Address</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Location</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Requests</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600 whitespace-nowrap">First Seen</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600 whitespace-nowrap">Last Seen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {currentStatsDetails.sessions_details.map((session, idx) => (
                          <tr key={idx} className="hover:bg-neutral-50">
                            <td className="px-4 py-3 font-mono text-xs text-neutral-600">{session.ip_address}</td>
                            <td className="px-4 py-3">
                              {session.user ? (
                                <div>
                                  <p className="font-medium text-neutral-800 text-xs">{session.user.full_name || session.user.email}</p>
                                  <p className="text-xs text-neutral-400">#{session.user.member_code}</p>
                                </div>
                              ) : (
                                <span className="text-neutral-400 text-xs">Guest</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-neutral-600">
                              {session.city && session.country ? `${session.city}, ${session.country}` : session.country || '-'}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-neutral-700">{session.request_count}</td>
                            <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDateTime(session.first_seen)}</td>
                            <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDateTime(session.last_seen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {currentStatsDetails.sessions_details.length === 0 && (
                      <p className="text-center text-neutral-500 py-8">No active sessions</p>
                    )}
                  </div>
                )}

                {/* Users Details */}
                {currentStatsModal === 'users' && currentStatsDetails.users_details && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">User</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Tier</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Location</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Requests</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600">Recent Pages</th>
                          <th className="px-4 py-3 text-left font-semibold text-neutral-600 whitespace-nowrap">Last Seen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {currentStatsDetails.users_details.map((user) => (
                          <tr key={user.id} className="hover:bg-neutral-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-neutral-800 text-sm">{user.full_name || 'User'}</p>
                                <p className="text-xs text-neutral-500">{user.email}</p>
                                <p className="text-xs text-primary-500">#{user.member_code}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                !['demo', 'explorer'].includes(user.subscription_tier)
                                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                                  : 'bg-neutral-100 text-neutral-700'
                              }`}>
                                {!['demo', 'explorer'].includes(user.subscription_tier) && <FaCrown className="inline mr-1 text-yellow-300" />}
                                {user.subscription_tier?.charAt(0).toUpperCase() + user.subscription_tier?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-neutral-600">
                              {user.city && user.country ? `${user.city}, ${user.country}` : user.country || '-'}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-neutral-700">{user.request_count}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1 max-w-[200px]">
                                {user.recent_pages?.slice(0, 3).map((page, idx) => (
                                  <span key={idx} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs truncate max-w-[80px]" title={page}>
                                    {page.split('/').pop() || '/'}
                                  </span>
                                ))}
                                {user.recent_pages?.length > 3 && (
                                  <span className="text-neutral-400 text-xs">+{user.recent_pages.length - 3}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDateTime(user.last_seen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {currentStatsDetails.users_details.length === 0 && (
                      <p className="text-center text-neutral-500 py-8">No logged-in users currently active</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-neutral-500 py-8">No data available</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Visitors Tab Content
  const VisitorsTab = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-medium p-4">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-neutral-400" />
          <h3 className="font-semibold text-neutral-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="IP Address"
            value={visitorFilters.ip}
            onChange={(e) => handleFilterChange('ip', e.target.value)}
            className="px-3 py-2 border-2 border-neutral-300 rounded-lg text-sm focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500"
          />
          <input
            type="text"
            placeholder="Path contains..."
            value={visitorFilters.path}
            onChange={(e) => handleFilterChange('path', e.target.value)}
            className="px-3 py-2 border-2 border-neutral-300 rounded-lg text-sm focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500"
          />
          <select
            value={visitorFilters.method}
            onChange={(e) => handleFilterChange('method', e.target.value)}
            className="px-3 py-2 border-2 border-neutral-300 rounded-lg text-sm focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500"
          >
            <option value="">All Methods</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input
            type="text"
            placeholder="Country Code (e.g., US)"
            value={visitorFilters.country}
            onChange={(e) => handleFilterChange('country', e.target.value.toUpperCase())}
            className="px-3 py-2 border-2 border-neutral-300 rounded-lg text-sm focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500"
          />
          <div className="flex gap-2">
            <button
              onClick={applyFilters}
              className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2"
            >
              <FaSearch /> Search
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-neutral-100 text-neutral-600 rounded-lg text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-medium overflow-hidden">
        <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-300 flex items-center justify-between">
          <h3 className="font-semibold text-neutral-700">
            Request Logs {visitorLogs && `(${visitorLogs.total.toLocaleString()} total)`}
          </h3>
          {visitorLoading && <FaSync className="text-cyan-500 animate-spin" />}
        </div>

        {visitorLogs && visitorLogs.logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-100">
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Date/Time</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Method</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Path</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">IP Address</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Location</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {visitorLogs.logs.map((log) => (
                    <tr key={log.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">
                        {formatDateTime(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {log.is_bot ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Bot
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            User
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <MethodBadge method={log.method} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-600 max-w-xs truncate" title={log.full_url}>
                        {log.path}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => loadIpDetails(log.ip_address)}
                          className="text-cyan-600 hover:text-cyan-800 hover:underline font-mono text-xs"
                        >
                          {log.ip_address}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600">
                        {log.city || log.region || log.country ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-neutral-700">
                              {[log.city, log.region].filter(Boolean).join(', ')}
                            </span>
                            <span className="text-neutral-500">
                              {log.country || log.country_code}
                            </span>
                          </div>
                        ) : log.country_code ? (
                          <span>{log.country_code}</span>
                        ) : (
                          <span className="text-neutral-400 italic">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={log.status_code} />
                      </td>
                      <td className="px-4 py-3 text-neutral-600 text-xs">
                        {log.response_time_ms}ms
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-300 flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Showing {visitorPage * LOGS_PER_PAGE + 1} - {Math.min((visitorPage + 1) * LOGS_PER_PAGE, visitorLogs.total)} of {visitorLogs.total.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setVisitorPage(Math.max(0, visitorPage - 1))}
                  disabled={visitorPage === 0}
                  className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronLeft />
                </button>
                <span className="text-sm text-neutral-600">
                  Page {visitorPage + 1} of {Math.ceil(visitorLogs.total / LOGS_PER_PAGE)}
                </span>
                <button
                  onClick={() => setVisitorPage(visitorPage + 1)}
                  disabled={(visitorPage + 1) * LOGS_PER_PAGE >= visitorLogs.total}
                  className="p-2 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="px-4 py-12 text-center text-neutral-500">
            {visitorLoading ? 'Loading...' : 'No visitor logs found'}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <FaSync className="text-4xl text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { users, itineraries, revenue, engagement, visitors } = data;

  // Calculate max rating count for bar chart scaling
  const ratingCounts = Object.values(engagement?.rating_distribution || {});
  const maxRatingCount = Math.max(...ratingCounts, 1);

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="bg-gradient-sunset rounded-2xl p-8 text-white shadow-elevated">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-3">
              <FaChartBar className="text-4xl" />
              Analytics Dashboard
            </h1>
            <p className="text-white/90 text-lg">ItinerarySeek performance metrics</p>
          </div>
          {/* Period Selector */}
          <div className="flex gap-2 flex-wrap">
            {[
              { value: null, label: 'All Time' },
              { value: 7, label: '7 Days' },
              { value: 30, label: '30 Days' },
              { value: 90, label: '90 Days' }
            ].map((option) => (
              <button
                key={option.value || 'all'}
                onClick={() => setPeriod(option.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  period === option.value
                    ? 'bg-white text-primary-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-neutral-100 rounded-t-xl">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center justify-center gap-2 px-6 py-4 font-semibold text-sm md:text-base transition-all ${
              activeTab === 'overview'
                ? 'text-primary-600 bg-white rounded-t-lg border-t-4 border-primary-500'
                : 'text-neutral-600 hover:text-primary-600'
            }`}
          >
            <FaChartBar /> Overview
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            className={`flex items-center justify-center gap-2 px-6 py-4 font-semibold text-sm md:text-base transition-all ${
              activeTab === 'visitors'
                ? 'text-cyan-600 bg-white rounded-t-lg border-t-4 border-cyan-500'
                : 'text-neutral-600 hover:text-cyan-600'
            }`}
          >
            <FaEye /> Visitor Logs
          </button>
        </div>
      </div>

      {activeTab === 'overview' ? (
        <>
          {/* Current Statistics (Real-time) */}
          <section className="mb-6">
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaSync className={`text-green-500 ${currentStatsLoading ? 'animate-spin' : ''}`} />
              Current Statistics
              <span className="ml-auto text-xs font-normal text-neutral-400">
                Auto-refreshes every 30s
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                onClick={() => openCurrentStatsModal('surfing')}
                className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-medium p-6 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Currently Surfing</p>
                    <p className="text-3xl font-bold mt-1">{currentStats?.currently_surfing ?? '-'}</p>
                    <p className="text-white/70 text-xs mt-1">Active in last 5 min</p>
                  </div>
                  <FaGlobe className="text-4xl text-white/30" />
                </div>
              </div>
              <div
                onClick={() => openCurrentStatsModal('sessions')}
                className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-medium p-6 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Current Sessions</p>
                    <p className="text-3xl font-bold mt-1">{currentStats?.current_sessions ?? '-'}</p>
                    <p className="text-white/70 text-xs mt-1">Active in last 15 min</p>
                  </div>
                  <FaNetworkWired className="text-4xl text-white/30" />
                </div>
              </div>
              <div
                onClick={() => openCurrentStatsModal('users')}
                className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl shadow-medium p-6 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Concurrent Users</p>
                    <p className="text-3xl font-bold mt-1">{currentStats?.concurrent_users ?? '-'}</p>
                    <p className="text-white/70 text-xs mt-1">Logged-in users now</p>
                  </div>
                  <FaUsers className="text-4xl text-white/30" />
                </div>
              </div>
            </div>
            {currentStats?.active_users_list?.length > 0 && (
              <div className="mt-4 bg-white rounded-xl shadow-medium p-4">
                <h3 className="font-semibold text-neutral-700 mb-3 text-sm">Active Users Right Now</h3>
                <div className="flex flex-wrap gap-2">
                  {currentStats.active_users_list.map((user) => (
                    <div key={user.id} className="flex items-center gap-2 bg-neutral-50 rounded-lg px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center text-white text-xs font-bold">
                        {user.full_name?.charAt(0) || user.email?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-800">{user.full_name || 'User'}</p>
                        <p className="text-xs text-neutral-500">#{user.member_code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Visitor Summary - hidden */}

          {/* Page Views Section */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaEye className="text-purple-500" />
              Page Views
              {siteStatsLoading && <FaSync className="text-purple-500 animate-spin ml-2" />}
            </h2>

            {/* Summary Cards */}
            {siteStats?.summary && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                  <p className="text-purple-100 text-sm">Total Page Views</p>
                  <p className="text-3xl font-bold">{siteStats.summary.total_page_views?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-4 text-white">
                  <p className="text-primary-100 text-sm">Unique Visitors</p>
                  <p className="text-3xl font-bold">{siteStats.summary.unique_visitors?.toLocaleString() || 0}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Pages */}
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                  <FaChartBar className="text-purple-400" />
                  Most Viewed Pages
                </h3>
                {siteStats?.top_pages?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {siteStats.top_pages.slice(0, 8).map((page, idx) => (
                      <div
                        key={idx}
                        onClick={() => loadPageDrilldown(page.path)}
                        className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-bold text-white bg-purple-500 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-neutral-800 truncate">{page.page_name || page.path}</p>
                          {page.recent_viewers?.length > 0 && (
                            <p className="text-xs text-neutral-500 mt-0.5">
                              Recent: {page.recent_viewers.slice(0, 2).map(v =>
                                v.user?.full_name || v.country || 'Visitor'
                              ).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-lg font-bold text-purple-600">{page.view_count || page.access_count}</span>
                          <span className="text-xs text-neutral-400 ml-1">views</span>
                          <p className="text-xs text-neutral-500">
                            (<span className="text-green-600">{page.user_count || 0}</span> users, <span className="text-orange-500">{page.bot_count || 0}</span> bots)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-8">
                    {siteStatsLoading ? 'Loading...' : 'No page views recorded yet'}
                  </p>
                )}
              </div>

              {/* Active Users */}
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                  <FaUsers className="text-primary-400" />
                  Recently Active Users
                </h3>
                {siteStats?.active_users?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {siteStats.active_users.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => loadUserActivity(item.user.id, item.user.full_name || item.user.email)}
                        className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg cursor-pointer hover:bg-primary-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-white font-bold text-sm">
                            {(item.user.full_name || item.user.email)?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-neutral-800 text-sm">{item.user.full_name || item.user.email}</p>
                            <p className="text-xs text-neutral-500 capitalize">{item.user.subscription_tier} plan</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-primary-600">{item.page_views || item.request_count} pages</p>
                          <p className="text-xs text-neutral-400">{formatDateTime(item.last_seen || item.last_activity)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-8">
                    {siteStatsLoading ? 'Loading...' : 'No active users yet'}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Blog Visitors */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaNewspaper className="text-teal-500" />
              Blog Visitors
              {blogVisitorsLoading && <FaSync className="text-teal-500 animate-spin ml-2" />}
              {(blogSelectedSlug || blogSelectedIp) && (
                <button
                  onClick={() => { setBlogSelectedSlug(null); setBlogSelectedIp(null); setBlogVisitorPage(0); }}
                  className="ml-auto text-sm font-normal text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
                >
                  <FaTimes className="text-xs" /> Clear Filters
                </button>
              )}
            </h2>

            {/* Summary Cards */}
            {blogVisitors?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
                  <p className="text-teal-100 text-sm">Total Blog Views</p>
                  <p className="text-3xl font-bold">{blogVisitors.summary.total_blog_views?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white">
                  <p className="text-cyan-100 text-sm">Unique Visitors</p>
                  <p className="text-3xl font-bold">{blogVisitors.summary.unique_blog_visitors?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                  <p className="text-blue-100 text-sm">Blog List Views</p>
                  <p className="text-3xl font-bold">{blogVisitors.summary.blog_list_views?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white">
                  <p className="text-indigo-100 text-sm">Article Views</p>
                  <p className="text-3xl font-bold">{blogVisitors.summary.blog_article_views?.toLocaleString() || 0}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Per-Article Breakdown */}
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                  <FaBookOpen className="text-teal-400" />
                  Articles by Views
                </h3>
                {blogVisitors?.articles?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {blogVisitors.articles.map((article, idx) => (
                      <div
                        key={idx}
                        onClick={() => { setBlogSelectedSlug(article.slug); setBlogSelectedIp(null); setBlogVisitorPage(0); }}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          blogSelectedSlug === article.slug ? 'bg-teal-50 ring-2 ring-teal-300' : 'bg-neutral-50 hover:bg-teal-50'
                        }`}
                      >
                        <span className="text-sm font-bold text-white bg-teal-500 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-neutral-800 truncate">{article.title}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {article.unique_visitors} unique visitor{article.unique_visitors !== 1 ? 's' : ''}
                            {article.last_visited && ` \u00b7 Last: ${formatDateTime(article.last_visited)}`}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-lg font-bold text-teal-600">{article.view_count}</span>
                          <span className="text-xs text-neutral-400 ml-1">views</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-8">
                    {blogVisitorsLoading ? 'Loading...' : 'No blog views recorded yet'}
                  </p>
                )}
              </div>

              {/* Unique IPs that visited blogs */}
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                  <FaNetworkWired className="text-cyan-400" />
                  Visitor IPs
                  {blogSelectedSlug && (
                    <span className="text-xs font-normal text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
                      /{blogSelectedSlug}
                    </span>
                  )}
                </h3>
                {blogVisitors?.unique_ips?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {blogVisitors.unique_ips.map((visitor, idx) => (
                      <div
                        key={idx}
                        onClick={() => { setBlogSelectedIp(visitor.ip_address); setBlogVisitorPage(0); setBlogVisitorsExpanded(true); }}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                          blogSelectedIp === visitor.ip_address ? 'bg-cyan-50 ring-2 ring-cyan-300' : 'bg-neutral-50 hover:bg-cyan-50'
                        }`}
                      >
                        <div>
                          <p className="font-mono text-sm font-medium text-neutral-800">{visitor.ip_address}</p>
                          <p className="text-xs text-neutral-500">
                            {[visitor.city, visitor.country].filter(Boolean).join(', ') || 'Unknown location'}
                            {` \u00b7 ${visitor.pages_viewed} page${visitor.pages_viewed !== 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-cyan-600">{visitor.visit_count} visit{visitor.visit_count !== 1 ? 's' : ''}</p>
                          <p className="text-xs text-neutral-400">{formatDateTime(visitor.last_visit)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-8">
                    {blogVisitorsLoading ? 'Loading...' : 'No blog visitors yet'}
                  </p>
                )}
              </div>
            </div>

            {/* Detailed Visit Log (expandable) */}
            {blogVisitors?.visitor_logs?.logs?.length > 0 && (
              <div className="mt-4 bg-white rounded-xl shadow-medium p-6">
                <h3
                  onClick={() => setBlogVisitorsExpanded(!blogVisitorsExpanded)}
                  className="font-semibold text-neutral-700 mb-4 flex items-center gap-2 cursor-pointer"
                >
                  <FaEye className="text-indigo-400" />
                  Visit Details
                  {blogSelectedIp && (
                    <span className="text-xs font-normal text-cyan-600 bg-cyan-50 px-2 py-1 rounded-full">
                      IP: {blogSelectedIp}
                    </span>
                  )}
                  {blogSelectedSlug && (
                    <span className="text-xs font-normal text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
                      /{blogSelectedSlug}
                    </span>
                  )}
                  <span className="ml-auto text-sm font-normal text-neutral-400">
                    {blogVisitors.visitor_logs.total} record{blogVisitors.visitor_logs.total !== 1 ? 's' : ''}
                    <FaChevronRight className={`inline ml-1 text-xs transition-transform ${blogVisitorsExpanded ? 'rotate-90' : ''}`} />
                  </span>
                </h3>
                {blogVisitorsExpanded && (
                  <>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-neutral-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-semibold text-neutral-600">IP Address</th>
                            <th className="px-3 py-2 font-semibold text-neutral-600">Page</th>
                            <th className="px-3 py-2 font-semibold text-neutral-600">Location</th>
                            <th className="px-3 py-2 font-semibold text-neutral-600">Date/Time</th>
                            <th className="px-3 py-2 font-semibold text-neutral-600">Referrer</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {blogVisitors.visitor_logs.logs.map((log) => (
                            <tr key={log.id} className={`hover:bg-neutral-50 ${log.is_bot ? 'opacity-50' : ''}`}>
                              <td className="px-3 py-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBlogSelectedIp(log.ip_address); setBlogVisitorPage(0); }}
                                  className="font-mono text-xs text-cyan-600 hover:text-cyan-800 hover:underline"
                                >
                                  {log.ip_address}
                                </button>
                                {log.is_bot && <span className="ml-1 text-xs text-orange-500">(bot)</span>}
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBlogSelectedSlug(log.slug || null); setBlogVisitorPage(0); }}
                                  className="text-teal-600 hover:text-teal-800 hover:underline truncate block max-w-[200px]"
                                >
                                  {log.path === '/blog' ? '/blog (list)' : `/${log.slug}`}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-neutral-600 text-xs">
                                {[log.city, log.country].filter(Boolean).join(', ') || '-'}
                              </td>
                              <td className="px-3 py-2 text-neutral-600 text-xs whitespace-nowrap">
                                {formatDateTime(log.created_at)}
                              </td>
                              <td className="px-3 py-2 text-neutral-500 text-xs truncate max-w-[150px]" title={log.referer || ''}>
                                {log.referer ? (() => { try { return new URL(log.referer).hostname; } catch { return log.referer; } })() : 'Direct'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {blogVisitors.visitor_logs.total > LOGS_PER_PAGE && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                        <span className="text-sm text-neutral-500">
                          Showing {blogVisitorPage * LOGS_PER_PAGE + 1}-{Math.min((blogVisitorPage + 1) * LOGS_PER_PAGE, blogVisitors.visitor_logs.total)} of {blogVisitors.visitor_logs.total}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setBlogVisitorPage(Math.max(0, blogVisitorPage - 1))}
                            disabled={blogVisitorPage === 0}
                            className="px-3 py-1 text-sm rounded bg-neutral-100 hover:bg-neutral-200 disabled:opacity-40"
                          >
                            <FaChevronLeft className="inline" /> Prev
                          </button>
                          <button
                            onClick={() => setBlogVisitorPage(blogVisitorPage + 1)}
                            disabled={(blogVisitorPage + 1) * LOGS_PER_PAGE >= blogVisitors.visitor_logs.total}
                            className="px-3 py-1 text-sm rounded bg-neutral-100 hover:bg-neutral-200 disabled:opacity-40"
                          >
                            Next <FaChevronRight className="inline" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          {/* User Metrics */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaUsers className="text-primary-500" />
              User Metrics
              <button
                onClick={() => openDrilldown('users')}
                className="ml-auto text-sm font-normal text-primary-600 hover:text-primary-800 flex items-center gap-1"
              >
                View All Users <FaChevronRight className="text-xs" />
              </button>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={FaUsers}
                label="Total Users"
                value={users?.total_users?.toLocaleString() || 0}
                color="primary"
                onClick={() => openDrilldown('users')}
              />
              <StatCard
                icon={FaUserPlus}
                label="New Signups"
                value={users?.new_signups?.toLocaleString() || 0}
                subValue={period ? `Last ${period} days` : 'All time'}
                color="green"
                onClick={() => openDrilldown('users')}
              />
              <StatCard
                icon={FaCheckCircle}
                label="Active Users"
                value={users?.active_users?.toLocaleString() || 0}
                subValue="Created itineraries"
                color="secondary"
                onClick={() => openDrilldown('users')}
              />
              <StatCard
                icon={FaCrown}
                label="Paid Users"
                value={Object.entries(users?.tier_breakdown || {})
                  .filter(([tier]) => tier !== 'explorer')
                  .reduce((sum, [, count]) => sum + count, 0)
                  .toLocaleString()}
                color="amber"
                onClick={() => openDrilldown('users', 'paid')}
              />
            </div>

            {/* Tier Breakdown */}
            <div className="mt-4 bg-white rounded-xl shadow-medium p-6">
              <h3 className="font-semibold text-neutral-700 mb-4">Subscription Tier Breakdown</h3>
              <div className="divide-y divide-neutral-100">
                {Object.entries(users?.tier_breakdown || {}).map(([tier, count]) => (
                  <TierBadge key={tier} tier={tier} count={count} onClick={() => openDrilldown('users', tier)} />
                ))}
              </div>
            </div>
          </section>

          {/* Itinerary Metrics */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaMapMarkedAlt className="text-secondary-500" />
              Itinerary Metrics
              <button
                onClick={() => openDrilldown('itineraries')}
                className="ml-auto text-sm font-normal text-secondary-600 hover:text-secondary-800 flex items-center gap-1"
              >
                View All Itineraries <FaChevronRight className="text-xs" />
              </button>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={FaMapMarkedAlt}
                label="Total Itineraries"
                value={itineraries?.total_itineraries?.toLocaleString() || 0}
                color="secondary"
                onClick={() => openDrilldown('itineraries')}
              />
              <StatCard
                icon={FaCheckCircle}
                label="Completion Rate"
                value={`${itineraries?.completion_rate || 0}%`}
                color="green"
                onClick={() => openDrilldown('itineraries', 'completed')}
              />
              <StatCard
                icon={FaClock}
                label="Avg Iterations"
                value={itineraries?.avg_optimization_iterations || 0}
                subValue="AI optimization rounds"
                color="purple"
                onClick={() => openDrilldown('itineraries')}
              />
              <StatCard
                icon={FaExclamationTriangle}
                label="Failed"
                value={itineraries?.status_breakdown?.failed || 0}
                color="pink"
                onClick={() => openDrilldown('itineraries', 'failed')}
              />
            </div>

            {/* Status Breakdown & Popular Destinations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4">Status Breakdown</h3>
                <div className="space-y-3">
                  {Object.entries(itineraries?.status_breakdown || {}).map(([status, count]) => {
                    const statusColors = {
                      completed: 'bg-green-500',
                      processing: 'bg-blue-500',
                      pending: 'bg-amber-500',
                      failed: 'bg-red-500'
                    };
                    const total = itineraries?.total_itineraries || 1;
                    const percentage = (count / total) * 100;
                    return (
                      <div
                        key={status}
                        className="flex items-center gap-3 cursor-pointer hover:bg-neutral-50 p-2 -mx-2 rounded-lg transition-colors"
                        onClick={() => openDrilldown('itineraries', status)}
                      >
                        <div className={`w-3 h-3 rounded-full ${statusColors[status] || 'bg-neutral-400'}`} />
                        <span className="text-sm font-medium text-neutral-600 capitalize flex-1">{status}</span>
                        <span className="text-sm text-neutral-500">{count}</span>
                        <span className="text-xs text-neutral-400 w-12 text-right">{percentage.toFixed(1)}%</span>
                        <FaChevronRight className="text-neutral-400 text-xs" />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4">Popular Destinations</h3>
                <div className="space-y-2">
                  {(itineraries?.popular_destinations || []).slice(0, 5).map((dest, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1">
                      <span className="text-sm text-neutral-600 truncate flex-1">
                        <span className="text-neutral-400 mr-2">#{idx + 1}</span>
                        {dest.destination}
                      </span>
                      <span className="text-sm font-semibold text-neutral-700 ml-2">{dest.count}</span>
                    </div>
                  ))}
                  {(!itineraries?.popular_destinations || itineraries.popular_destinations.length === 0) && (
                    <p className="text-sm text-neutral-400 text-center py-4">No destination data yet</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Revenue Metrics */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaDollarSign className="text-green-500" />
              Revenue Metrics
              <button
                onClick={() => openDrilldown('subscriptions')}
                className="ml-auto text-sm font-normal text-green-600 hover:text-green-800 flex items-center gap-1"
              >
                View All Events <FaChevronRight className="text-xs" />
              </button>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={FaCrown}
                label="Active Subscriptions"
                value={revenue?.active_subscriptions?.toLocaleString() || 0}
                color="amber"
                onClick={() => openDrilldown('subscriptions')}
              />
              <StatCard
                icon={FaDollarSign}
                label="Monthly Billing"
                value={revenue?.billing_distribution?.monthly || 0}
                color="green"
                onClick={() => openDrilldown('subscriptions')}
              />
              <StatCard
                icon={FaDollarSign}
                label="Yearly Billing"
                value={revenue?.billing_distribution?.yearly || 0}
                color="primary"
                onClick={() => openDrilldown('subscriptions')}
              />
              <StatCard
                icon={FaExclamationTriangle}
                label="Cancellations"
                value={revenue?.cancellations || 0}
                subValue={period ? `Last ${period} days` : 'All time'}
                color="pink"
                onClick={() => openDrilldown('subscriptions', 'cancelled')}
              />
            </div>

            {/* Subscription Events */}
            <div className="mt-4 bg-white rounded-xl shadow-medium p-6">
              <h3 className="font-semibold text-neutral-700 mb-4">Subscription Events</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(revenue?.event_breakdown || {}).map(([event, count]) => (
                  <div
                    key={event}
                    className="text-center p-3 bg-neutral-50 rounded-lg cursor-pointer hover:bg-neutral-100 transition-colors"
                    onClick={() => openDrilldown('subscriptions', event)}
                  >
                    <p className="text-lg font-bold text-neutral-800">{count}</p>
                    <p className="text-xs text-neutral-500 capitalize">{event.replace('_', ' ')}</p>
                  </div>
                ))}
                {Object.keys(revenue?.event_breakdown || {}).length === 0 && (
                  <p className="col-span-4 text-sm text-neutral-400 text-center py-4">No subscription events yet</p>
                )}
              </div>
            </div>
          </section>

          {/* Engagement Metrics */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <FaStar className="text-amber-500" />
              Engagement Metrics
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={FaStar}
                label="Total Ratings"
                value={engagement?.total_ratings?.toLocaleString() || 0}
                subValue={`Avg: ${engagement?.average_rating || 0}/5`}
                color="amber"
                onClick={() => openDrilldown('ratings')}
              />
              <StatCard
                icon={FaRobot}
                label="AI Chat Messages"
                value={engagement?.ai_chat_messages?.toLocaleString() || 0}
                subValue={`${engagement?.ai_modifications_applied || 0} applied`}
                color="purple"
                onClick={() => openDrilldown('chat-modifications')}
              />
              <StatCard
                icon={FaCamera}
                label="Photo Uploads"
                value={engagement?.photo_uploads?.toLocaleString() || 0}
                color="pink"
                onClick={() => openDrilldown('photos')}
              />
              <StatCard
                icon={FaComments}
                label="Group Messages"
                value={engagement?.group_messages?.toLocaleString() || 0}
                color="secondary"
                onClick={() => openDrilldown('group-activity', 'messages')}
              />
            </div>

            {/* Rating Distribution & Group Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center justify-between">
                  Rating Distribution
                  <button
                    onClick={() => openDrilldown('ratings')}
                    className="text-xs font-normal text-amber-600 hover:text-amber-800 flex items-center gap-1"
                  >
                    View All <FaChevronRight className="text-xs" />
                  </button>
                </h3>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <div
                      key={rating}
                      className="cursor-pointer hover:bg-neutral-50 rounded p-1 -mx-1 transition-colors"
                      onClick={() => openDrilldown('ratings', rating)}
                    >
                      <RatingBar
                        rating={rating}
                        count={engagement?.rating_distribution?.[String(rating)] || 0}
                        maxCount={maxRatingCount}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-medium p-6">
                <h3 className="font-semibold text-neutral-700 mb-4 flex items-center justify-between">
                  Group Collaboration
                  <button
                    onClick={() => openDrilldown('group-activity')}
                    className="text-xs font-normal text-secondary-600 hover:text-secondary-800 flex items-center gap-1"
                  >
                    View All <FaChevronRight className="text-xs" />
                  </button>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className="text-center p-4 bg-neutral-50 rounded-lg cursor-pointer hover:bg-neutral-100 transition-colors"
                    onClick={() => openDrilldown('group-activity', 'invites')}
                  >
                    <p className="text-2xl font-bold text-neutral-800">{engagement?.group_invites || 0}</p>
                    <p className="text-sm text-neutral-500">Total Invites</p>
                  </div>
                  <div
                    className="text-center p-4 bg-neutral-50 rounded-lg cursor-pointer hover:bg-neutral-100 transition-colors"
                    onClick={() => openDrilldown('group-activity', 'invites')}
                  >
                    <p className="text-2xl font-bold text-neutral-800">{engagement?.group_invites_accepted || 0}</p>
                    <p className="text-sm text-neutral-500">Accepted</p>
                  </div>
                  <div className="col-span-2 text-center p-4 bg-primary-50 rounded-lg">
                    <p className="text-2xl font-bold text-primary-600">
                      {engagement?.group_invites > 0
                        ? ((engagement?.group_invites_accepted / engagement?.group_invites) * 100).toFixed(1)
                        : 0}%
                    </p>
                    <p className="text-sm text-primary-500">Acceptance Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <VisitorsTab />
      )}

      {/* Footer */}
      <div className="text-center text-sm text-neutral-400 pt-4 border-t border-neutral-100">
        Last updated: {formatDateTime(data.generated_at)}
      </div>

      {/* IP Details Modal */}
      <IpDetailsPanel />

      {/* Drill-down Modal */}
      <DrilldownModal />

      {/* Page Drilldown Modal */}
      <PageDrilldownModal />

      {/* User Activity Modal */}
      <UserActivityModal />

      {/* Current Stats Modal */}
      <CurrentStatsModal />
    </div>
  );
};

export default Analytics;
